import { Button, Col, DesignTokens, PlusIcon, Row, Space, Title } from "melco-ui";
import styled from "styled-components";
import { useState } from "react";
import { Formik, FormikHelpers } from "formik";
import { useMachineStatusView } from "../../../hooks/reporting/useMachineStatusView";
import { useMachineStatusViewEdit } from "../../../hooks/reporting/useMachineStatusViewEdit";
import { EditViewButton } from "./EditViewButton";
import { MachineStatusAddMachineGroupDrawer } from "./MachineStatusAddMachineGroupDrawer";
import { useTranslation } from "react-i18next";
import {
  ReportingMelcoMachineStatisticsModelsView,
  ReportingMelcoMachineStatisticsModelsViewListItem,
  useFormSubmit,
  useGenericDrawer,
} from "melco-shared-logic";
import { ViewerLinkButton } from "./viewerLink/ViewerLinkButton";
import { ViewerLinkDrawer } from "./viewerLink/ViewerLinkDrawer";
import { useViewerLinkAdd } from "../../../hooks/reporting/useViewerLinkAdd";
import { DisplayMachineStatusViewGroupsAndMachies } from "./DisplayMachineStatusViewGroupsAndMachines";
import { useNavigate } from "react-router";
import LoadingDefaultSpinIndicator from "../../loading/LoadingDefaultSpinIndicator";

type MachineStatusViewProps = {
  view: ReportingMelcoMachineStatisticsModelsViewListItem;
  activeTooltip: string | null;
  setActiveTooltip: React.Dispatch<React.SetStateAction<string | null>>;
};

const { colors, spacings } = DesignTokens;

// Styled container for buttons
const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const EmptyMachineGroupWrapper = styled.div`
  border: 1px dashed ${colors.gray.middle};
  padding: ${spacings.middle}px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MachineStatusView: React.FC<MachineStatusViewProps> = ({
  view,
  activeTooltip,
  setActiveTooltip,
}) => {
  const { t } = useTranslation();
  let {
    data: viewDetail,
    isLoading,
    refetch,
  } = useMachineStatusView(view?.id ?? null);
  //viewDetail = {"id":"VW-4","name":"V1","grouped_display":true,"group_count":2,"group_list":[{"id":"GRP-1","name":"G1","machine_count":2,"machine_list":[{"serial_number":"IG1004","name":"IG1004","last_status":"1"},{"serial_number":"IG1003","name":"IG1003","last_status":"2"}]},{"id":"GRP-2","name":"G5","machine_count":5,"machine_list":[{"serial_number":"IG1004","name":"IG1004","last_status":"3"},{"serial_number":"IG1003","name":"IG1003","last_status":"1"},{"serial_number":"IG1002","name":"IG1002","last_status":"2"},{"serial_number":"IG1005","name":"IG1005","last_status":"3"},{"serial_number":"IG1001","name":"IG1001","last_status":"1"}]}]};

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const editStatusView = useMachineStatusViewEdit();
  const addViewerLink = useViewerLinkAdd();
  const viewerLinkDrawer = useGenericDrawer();

  const {
    closeDrawer: closeViewerLinkDrawer,
    isDrawerOpen: isViewerLinkDrawerOpen,
    openDrawer: openViewerLinkDrawer,
  } = viewerLinkDrawer;

  const navigate = useNavigate();

  const onHandleSubmit = useFormSubmit(
    async (values: any, formikHelpers: FormikHelpers<any>) => {
      //console.log("statusView ", view);
      const { submitAction } = values;

      if (submitAction == "view") {
        if (view && view.id) {
          const updatedView = await editStatusView(values);
          navigate("/reporting/machine/status/views/" + view.id);
        } else {
        }
      } else {
        await addViewerLink(values);
        refetch();
      }
      return;
    },
    {
      translationPrefix: "",
    }
  );

  const InitialValues = (
    statusView: ReportingMelcoMachineStatisticsModelsView | undefined
  ) => {
    const { name, id, group_list, grouped_display } = statusView
      ? statusView
      : {
        name: "",
        id: "",
        group_list: [],
        grouped_display: false,
      };

    const initialValues: any = {
      name: name ?? "",
      id: id ?? "",
      group_list: group_list ?? [],
      grouped_display: grouped_display ? true : false,
      submitAction: "view",
      days: undefined,
      password: "",
    };
    return initialValues;
  };

  const addMachineGroupButton = (
    <>
      <Row >
        <Col xs={24} md={24} xl={24}>
          <div style={{ textAlign: "center" }}>
            {t("reporting.machinestatus.no_machine_groups_added")}
          </div>
        </Col>
        <Col xs={24} md={24} xl={24}>
          <div style={{ textAlign: "center" }}>
            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              {t("reporting.machinestatus.add_machine_group_button")}
            </Button>
          </div>
        </Col>
      </Row>
    </>
  );

  return (
    <Formik
      initialValues={InitialValues(viewDetail)}
      enableReinitialize
      onSubmit={(values, formikHelpers) =>
        onHandleSubmit(values, formikHelpers)
      }
    >
      <>
        <LoadingDefaultSpinIndicator isShown={isLoading}>
          <Row justify="space-between"
          //style={{ margin: "0px", padding: "0px", }}
          >
            <Col >
              <div >
                <Title level={4}>{view.name}</Title>
              </div>
            </Col>
            <Col >
              <Space size="middle">
                <ViewerLinkButton onClick={openViewerLinkDrawer} />
                <EditViewButton viewId={view?.id || ""} />
              </Space>
            </Col>
          </Row>

          {viewDetail && (
            <DisplayMachineStatusViewGroupsAndMachies
              viewDetailObj={viewDetail}
              activeTooltip={activeTooltip}
              setActiveTooltip={setActiveTooltip}
            />
          )}

          {viewDetail?.group_list && viewDetail?.group_list?.length == 0 && (
            <>
              <EmptyMachineGroupWrapper>
                {addMachineGroupButton}
              </EmptyMachineGroupWrapper>
              {isDrawerOpen && (
                <MachineStatusAddMachineGroupDrawer
                  isDrawerOpen={isDrawerOpen}
                  setDrawerOpen={setDrawerOpen}
                  addAndSave={true}
                />
              )}
            </>
          )}

          {isViewerLinkDrawerOpen && (
            <ViewerLinkDrawer
              drawer={viewerLinkDrawer}
              viewId={viewDetail?.id}
            />
          )}
        </LoadingDefaultSpinIndicator>
      </>
    </Formik>
  );
};
